/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import styles from '../../../../styles/components/pageComponents/coach/AssignmentForm.module.css';

interface AssignmentData {
	id: string;
	title: string;
	type: string;
	dueDate: string;
	isVisible: boolean;
}

interface AssignmentFormProps {
	modalTitle?: string;
	isEditMode?: boolean;
	labels?: {
		title?: string;
		type?: string;
		dueDate?: string;
		visibility?: string;
	};
	placeholders?: {
		title?: string;
		typeSelect?: string;
		date?: string;
	};
	buttonTexts?: {
		addMore?: string;
		submit?: string;
	};
	initialData?: {
		title: string;
		dueDate: string | Date;
		type: string;
		isVisible: boolean;
	};
	assignmentOptions?: string[];
	assignmentCreateErrors?: { [id: string]: string };
	onSubmit: (assignments: AssignmentData[]) => void | Promise<void>;
	onClose?: () => void;
}

const AssignmentForm: React.FC<AssignmentFormProps> = ({
	isEditMode = false,
	modalTitle = 'Create Assignment',
	labels = {
		title: 'Assignment Title',
		type: 'Assignment Type',
		dueDate: 'Due Date',
		visibility: 'Visible to Learners',
	},
	placeholders = {
		title: 'Enter assignment title',
		typeSelect: 'Select file type',
		date: 'DD/MM/YYYY',
	},
	buttonTexts = {
		addMore: 'Add another assignment',
		submit: 'Create Assignment',
	},
	initialData,
	assignmentOptions = ['PDF', 'Video', 'Document', 'Image', 'Audio', 'Spreadsheet', 'Presentation'],
	assignmentCreateErrors = {},
	onSubmit,
	onClose,
}) => {
	/* ---------------- INITIAL STATE ---------------- */

	const getEmptyAssignment = (): AssignmentData => ({
		id: crypto.randomUUID(),
		title: '',
		type: '',
		dueDate: '',
		isVisible: false,
	});

	const [errors, setErrors] = useState<{
		[key: string]: {
			title?: string;
			type?: string;
			dueDate?: string;
		};
	}>({});

	const validate = () => {
		const newErrors: typeof errors = {};
		const titles = assignments
			.map(a => a.title.trim().toLowerCase())
			.filter(Boolean);
		const duplicates = new Set(
			titles.filter((t, i) => titles.indexOf(t) !== i)
		);

		assignments.forEach(assignment => {
			const rowErrors: {
				title?: string;
				type?: string;
				dueDate?: string;
			} = {};

const trimmedTitle = assignment.title.trim();
const titleLength = trimmedTitle.replace(/\s/g, '').length;

if (!trimmedTitle) {
	rowErrors.title = 'Assignment title is required';
} else if (titleLength < 3) {
	rowErrors.title =
		'Assignment title must be at least 3 characters';
} else if (duplicates.has(trimmedTitle.toLowerCase())) {
	rowErrors.title = 'Duplicate assignment title';
}

			if (!assignment.type) {
				rowErrors.type = 'Assignment type is required';
			}

			if (!assignment.dueDate) {
				rowErrors.dueDate = 'Due date is required';
			}

			if (Object.keys(rowErrors).length > 0) {
				newErrors[assignment.id] = rowErrors;
			}
		});

		setErrors(newErrors);

		return Object.keys(newErrors).length === 0;
	};

	const [assignments, setAssignments] = useState<AssignmentData[]>(() => {
		if (initialData) {
			return [
				{
					id: crypto.randomUUID(),
					title: initialData.title,
					type: initialData.type,
					dueDate: initialData.dueDate
						? new Date(initialData.dueDate)
								.toISOString()
								.split('T')[0]
						: '',
					isVisible: initialData.isVisible,
				},
			];
		}
		return [getEmptyAssignment()];
	});

	/* ---------------- SYNC WHEN EDIT MODE CHANGES ---------------- */

	useEffect(() => {
		if (initialData) {
			setAssignments([
				{
					id: crypto.randomUUID(),
					title: initialData.title,
					type: initialData.type,
					dueDate: initialData.dueDate
						? new Date(initialData.dueDate)
								.toISOString()
								.split('T')[0]
						: '',
					isVisible: initialData.isVisible,
				},
			]);
		} else {
			setAssignments([getEmptyAssignment()]);
		}
	}, [initialData]);

	/* ---------------- HANDLERS ---------------- */

const handleChange = (
	id: string,
	field: keyof AssignmentData,
	value: any
) => {
	setAssignments(prev =>
		prev.map(item =>
			item.id === id ? { ...item, [field]: value } : item
		)
	);

	setErrors(prev => {
		const updated = {
			...prev,
			[id]: {
				...prev[id],
				[field]: undefined,
			},
		};

		// Live validation for Assignment Title
		if (field === 'title') {
			const trimmedLength = String(value).replace(/\s/g, '').length;

			if (trimmedLength === 0) {
				updated[id].title = 'Assignment title is required';
			} else if (trimmedLength < 3) {
				updated[id].title =
					'Assignment title must be at least 3 characters';
			}
		}

		return updated;
	});
};

	const addRow = () => {
		setAssignments(prev => [...prev, getEmptyAssignment()]);
	};

	const removeRow = (id: string) => {
		setAssignments(prev => prev.filter(item => item.id !== id));
		setErrors(prev => {
			const updated = { ...prev };
			delete updated[id];
			return updated;
		});
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!isOnline) {
			// alert("Please connect to the internet to create assignment.");
			return;
		}

		const isValid = validate();
		if (!isValid) return;

		await onSubmit(assignments);
	};

	const [isOnline, setIsOnline] = useState(navigator.onLine);
	useEffect(() => {
		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);

		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);

		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

const isSubmitDisabled =
	!isOnline ||
	assignments.some(assignment => {
		const titleLength = assignment.title
			.trim()
			.replace(/\s/g, '').length;

		return titleLength < 3;
	});


	/* ---------------- RENDER ---------------- */

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h2 className={styles.title}>
					{isEditMode ? 'Update Assignment' : modalTitle}
				</h2>

				{onClose && (
					<button
						type="button"
						className={styles.closeBtn}
						onClick={onClose}
						aria-label="Close"
					>
						✕
					</button>
				)}
			</div>

			{!isEditMode && Object.keys(assignmentCreateErrors).length > 0 && (
				<div className={styles.creationErrorBanner}>
					<p className={styles.bannerTitle}>
						Failed to Create Some Assignments
					</p>
					<p className={styles.bannerText}>
						Remove failed items below and try again, or fix the
						errors.
					</p>
				</div>
			)}

			<form className={styles.form} onSubmit={handleSubmit}>
				{assignments.map(assignment => (
					<div
						key={assignment.id}
						className={`${styles.row} ${assignmentCreateErrors[assignment.id] ? styles.failedRow : ''}`}
					>
						{assignmentCreateErrors[assignment.id] && (
							<div className={styles.rowCreationError}>
								<span>
									⚠️ {assignmentCreateErrors[assignment.id]}
								</span>
							</div>
						)}
						<div className={styles.fieldGroup}>
							<label>{labels.title}</label>
							<input
								type="text"
								placeholder={placeholders.title}
								value={assignment.title}
								onChange={e =>
									handleChange(
										assignment.id,
										'title',
										e.target.value
									)
								}
							/>
							{errors[assignment.id]?.title && (
								<span className={styles.errorText}>
									{errors[assignment.id].title}
								</span>
							)}
						</div>

						<div className={styles.fieldGroup}>
							<label>{labels.type}</label>
							<select
								value={assignment.type}
								onChange={e =>
									handleChange(
										assignment.id,
										'type',
										e.target.value
									)
								}
							>
								<option value="" disabled>
									{placeholders.typeSelect}
								</option>
								{assignmentOptions.map(opt => (
									<option key={opt} value={opt}>
										{opt}
									</option>
								))}
							</select>

							{errors[assignment.id]?.type && (
								<span className={styles.errorText}>
									{errors[assignment.id].type}
								</span>
							)}
						</div>

						<div className={styles.fieldGroup}>
							<label>{labels.dueDate}</label>
							<input
								type="date"
								min={new Date().toISOString().split('T')[0]} // 🔥 blocks past dates
								value={assignment.dueDate}
								onChange={e =>
									handleChange(
										assignment.id,
										'dueDate',
										e.target.value
									)
								}
								required
							/>

							{errors[assignment.id]?.dueDate && (
								<span className={styles.errorText}>
									{errors[assignment.id].dueDate}
								</span>
							)}
						</div>

						<div className={styles.visibilityRow}>
							<div className={styles.fieldGroupCentered}>
								<label>{labels.visibility}</label>
								<input
									className={styles.checkbox}
									type="checkbox"
									checked={assignment.isVisible}
									onChange={e =>
										handleChange(
											assignment.id,
											'isVisible',
											e.target.checked
										)
									}
								/>
							</div>

							{!isEditMode && assignments.length > 1 && (
								<button
									type="button"
									className={styles.removeBtn}
									onClick={() => removeRow(assignment.id)}
									title="Remove this assignment"
									aria-label="Remove assignment"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="40"
										height="40"
										viewBox="0 0 40 40"
										fill="none"
									>
										<rect
											x="0.5"
											y="0.5"
											width="39"
											height="39"
											rx="4.5"
											fill="white"
										/>
										<rect
											x="0.5"
											y="0.5"
											width="39"
											height="39"
											rx="4.5"
											stroke="#D9D9D9"
										/>
										<path
											d="M18 19V25M22 19V25M27 14V28C27 28.5304 26.7893 29.0391 26.4142 29.4142C26.0391 29.7893 25.5304 30 25 30H15C14.4696 30 13.9609 29.7893 13.5858 29.4142C13.2107 29.0391 13 28.5304 13 28V14M11 14H29M16 14V12C16 11.4696 16.2107 10.9609 16.5858 10.5858C16.9609 10.2107 17.4696 10 18 10H22C22.5304 10 23.0391 10.2107 23.4142 10.5858C23.7893 10.9609 24 11.4696 24 12V14"
											stroke="black"
											stroke-width="1.5"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</button>
							)}
						</div>
					</div>
				))}
			</form>

			<div className={styles.footer}>
				{/* Add row only in Create mode */}
				{!isEditMode && (
					<button
						type="button"
						className={styles.addBtn}
						onClick={addRow}
					>
						<span className={styles.plusIcon}>+</span>
						{buttonTexts.addMore}
					</button>
				)}

				<button
					type="submit"
					className={styles.submitBtn}
					onClick={handleSubmit}
					disabled={!isOnline || isSubmitDisabled}
				>
					{isEditMode ? 'Update Assignment' : buttonTexts.submit}
				</button>
				{!isOnline && (
					<div className={styles.networkError}>
						Please connect to the internet to create assignment.
					</div>
				)}
			</div>
		</div>
	);
};

export default AssignmentForm;
